import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { determineNoteSource, findOrCreateNoteForSource, extractTextFromTiptapContent } from "@/lib/notes/noteUtils";
import { markdownToTipTap } from "@/lib/utils/formatConversion";
import { apiRequest, buildApiUrl, secureFetch } from "@/lib/queryClient";

interface NotesMetadata {
  source?: 'session' | 'lesson';
  session_id?: string;
  session_name?: string;
}

interface NotesContextType {
  isNotesOpen: boolean;
  openNotes: () => void;
  closeNotes: () => void;
  toggleNotes: () => void;
  captureToNote: (content: string, sourceLink?: string, sessionId?: string) => Promise<void>;
  appendToNote: (content: string, sourceTitle: string, sourceLink?: string, metadata?: NotesMetadata, folderId?: string | null) => Promise<void>;
  createNewNote: (content: string, sourceTitle: string, sourceLink?: string, metadata?: NotesMetadata, folderId?: string | null) => Promise<void>;
  activeNoteId: string | null;
  setActiveNoteId: (id: string | null) => void;
  activeNoteSessionId: string | null;
  startNoteSession: (noteId: string) => Promise<void>;
  endNoteSession: () => Promise<void>;
  getNoteForSource: (sourceType: string, sourceId: string) => Promise<{ id: string; title: string } | null>;
  openOrCreateNoteForSource: (sourceType: string, sourceId: string, sourceName: string, initialContent?: string) => Promise<void>;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error("useNotes must be used within NotesProvider");
  }
  return context;
};

interface NotesProviderProps {
  children: ReactNode;
}

export const NotesProvider = ({ children }: NotesProviderProps) => {
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [activeNoteSessionId, setActiveNoteSessionId] = useState<string | null>(null);
  const activeNoteIdForSessionRef = useRef<string | null>(null);

  const openNotes = () => setIsNotesOpen(true);
  const closeNotes = () => setIsNotesOpen(false);
  const toggleNotes = () => setIsNotesOpen(prev => !prev);

  const startSessionMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const response = await apiRequest(`/api/notes/${noteId}/sessions/start`, {
        method: 'POST',
      });
      return response as { id: string };
    },
  });

  const endSessionMutation = useMutation({
    mutationFn: async ({ noteId, sessionId }: { noteId: string; sessionId: string }) => {
      await apiRequest(`/api/notes/${noteId}/sessions/${sessionId}/end`, {
        method: 'POST',
      });
    },
  });

  const startSessionMutationRef = useRef(startSessionMutation);
  const endSessionMutationRef = useRef(endSessionMutation);
  const activeNoteSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    startSessionMutationRef.current = startSessionMutation;
    endSessionMutationRef.current = endSessionMutation;
  }, [startSessionMutation, endSessionMutation]);

  useEffect(() => {
    activeNoteSessionIdRef.current = activeNoteSessionId;
  }, [activeNoteSessionId]);

  const startNoteSession = useCallback(async (noteId: string) => {
    if (activeNoteIdForSessionRef.current === noteId && activeNoteSessionIdRef.current) {
      return;
    }

    if (activeNoteSessionIdRef.current && activeNoteIdForSessionRef.current) {
      try {
        await endSessionMutationRef.current.mutateAsync({
          noteId: activeNoteIdForSessionRef.current,
          sessionId: activeNoteSessionIdRef.current,
        });
      } catch (error) {
        console.error('Failed to end previous note session:', error);
      }
    }

    try {
      const result = await startSessionMutationRef.current.mutateAsync(noteId);
      setActiveNoteSessionId(result.id);
      activeNoteIdForSessionRef.current = noteId;
    } catch (error) {
      console.error('Failed to start note session:', error);
    }
  }, []);

  const endNoteSession = useCallback(async () => {
    if (!activeNoteSessionIdRef.current || !activeNoteIdForSessionRef.current) {
      return;
    }

    try {
      await endSessionMutationRef.current.mutateAsync({
        noteId: activeNoteIdForSessionRef.current,
        sessionId: activeNoteSessionIdRef.current,
      });
    } catch (error) {
      console.error('Failed to end note session:', error);
    } finally {
      setActiveNoteSessionId(null);
      activeNoteIdForSessionRef.current = null;
    }
  }, []);

  const captureToNote = async (
    content: string,
    sourceLink?: string,
    sessionId?: string
  ) => {
    const { trackNoteEvent } = await import("@/lib/noteAnalytics");
    const { generateTags } = await import("@/lib/aiTagging");
    const { toast } = await import("sonner");

    try {
      const now = new Date();
      const title = `Session Note — ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
      const autoTags = await generateTags(content);

      const response = await secureFetch(buildApiUrl('/api/notes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          tags: autoTags,
          content: JSON.stringify({
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [{ type: 'text', text: content }],
              },
            ],
          }),
          source_link: sourceLink,
        }),
      });

      if (!response.ok) {
        toast.error("Failed to save note");
        return;
      }

      const note = await response.json();

      trackNoteEvent({
        eventType: 'note_captured',
        noteId: note.id,
        sessionId,
      });

      toast.success("Saved to Notes");
      setActiveNoteId(note.id);
      openNotes();
    } catch (error) {
      toast.error("Failed to save note");
    }
  };

  const appendToNote = async (
    content: string,
    sourceTitle: string,
    sourceLink?: string,
    metadata?: NotesMetadata,
    folderId?: string | null
  ) => {
    const { trackNoteEvent } = await import("@/lib/noteAnalytics");
    const { toast } = await import("sonner");

    const now = new Date();
    const timestamp = now.toLocaleString();

    // Build source info based on metadata
    let sourceInfo = sourceTitle;
    if (metadata?.source === 'session' && metadata.session_name) {
      sourceInfo = metadata.session_name;
    }

    // Convert content to TipTap format preserving formatting
    const formattedContent = markdownToTipTap(content);

    // Create divider and metadata content
    const appendContent = {
      type: 'doc',
      content: [
        { type: 'horizontalRule' },
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: '📘 Added from: ', marks: [{ type: 'bold' }] },
            { type: 'text', text: sourceInfo },
          ],
        },
        ...formattedContent,
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: `(Saved at ${timestamp})`, marks: [{ type: 'italic' }] },
          ],
        },
      ],
    };

    try {
      let noteId = activeNoteId;

      // Determine the source for this note
      const source = determineNoteSource(metadata);

      if (!noteId) {
        const response = await secureFetch(buildApiUrl('/api/notes/find-or-create'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceType: source.type,
            sourceId: source.id,
            sourceName: source.name,
            content,
            folderId,
          }),
        });

        if (!response.ok) {
          toast.error("Failed to create note");
          return;
        }

        const newNote = await response.json();
        noteId = newNote.id;
        
        setActiveNoteId(noteId);
        trackNoteEvent({ eventType: 'note_viewed', noteId, metadata: { original_event: 'note_auto_created', source: source.type, folder_id: folderId } });
      } else {
        const response = await secureFetch(buildApiUrl(`/api/notes/${noteId}`));
        if (!response.ok) {
          toast.error("Failed to find note");
          return;
        }

        const currentNote = await response.json();

        // If sources don't match, find or create the correct note
        if (currentNote && (currentNote.source_type !== source.type || currentNote.source_id !== source.id)) {
          const findResponse = await secureFetch(buildApiUrl('/api/notes/find-or-create'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sourceType: source.type,
              sourceId: source.id,
              sourceName: source.name,
              content,
            }),
          });

          if (!findResponse.ok) {
            toast.error("Failed to create note");
            return;
          }

          const newNote = await findResponse.json();
          noteId = newNote.id;
          setActiveNoteId(noteId);
        }
      }

      const response = await secureFetch(buildApiUrl(`/api/notes/${noteId}/append`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appendContent,
          sourceLink,
        }),
      });

      if (!response.ok) {
        toast.error("Failed to append to note");
        trackNoteEvent({ eventType: 'note_viewed', metadata: { error: 'note_append_error' } });
        return;
      }

      trackNoteEvent({ eventType: 'note_viewed', noteId, metadata: { original_event: 'note_append_success', source: source.type } });

      // Debounced toast - only show once per minute
      const lastToast = localStorage.getItem('lastNoteToast');
      const shouldShowToast = !lastToast || (now.getTime() - parseInt(lastToast)) > 60000;
      
      if (shouldShowToast) {
        toast.success("Saved to Notes");
        localStorage.setItem('lastNoteToast', now.getTime().toString());
      }
      
      openNotes();
    } catch (error) {
      console.error('Failed to append to note:', error);
      toast.error("Failed to save to note");
    }
  };

  const createNewNote = async (
    content: string,
    sourceTitle: string,
    sourceLink?: string,
    metadata?: NotesMetadata,
    folderId?: string | null
  ) => {
    const { trackNoteEvent } = await import("@/lib/noteAnalytics");
    const { generateTags } = await import("@/lib/aiTagging");
    const { toast } = await import("sonner");

    const now = new Date();
    const timestamp = now.toLocaleString();

    // Build source info based on metadata
    let sourceInfo = sourceTitle;
    if (metadata?.source === 'session' && metadata.session_name) {
      sourceInfo = metadata.session_name;
    }

    // Convert content to TipTap format preserving formatting
    const formattedContent = markdownToTipTap(content);

    // Generate auto tags
    const autoTags = await generateTags(content);

    // Create new note content
    const noteContent = {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: '📘 From: ', marks: [{ type: 'bold' }] },
            { type: 'text', text: sourceInfo },
          ],
        },
        ...formattedContent,
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: `(Saved at ${timestamp})`, marks: [{ type: 'italic' }] },
          ],
        },
      ],
    };

    try {
      const response = await secureFetch(buildApiUrl('/api/notes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${sourceInfo} — ${now.toLocaleDateString()}`,
          tags: autoTags,
          content: JSON.stringify(noteContent),
          sourceLink,
          folderId,
          sourceType: metadata?.source || null,
          sourceId: metadata?.session_id || null,
          sourceName: sourceInfo,
        }),
      });

      if (!response.ok) {
        toast.error("Failed to create note");
        return;
      }

      const note = await response.json();

      setActiveNoteId(note.id);
      trackNoteEvent({ 
        eventType: 'note_viewed', 
        noteId: note.id, 
        metadata: { original_event: 'note_created', source: metadata?.source } 
      });

      toast.success("Created new note");
      openNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error("Failed to create note");
    }
  };

  const getNoteForSource = useCallback(async (
    sourceType: string,
    sourceId: string
  ): Promise<{ id: string; title: string } | null> => {
    try {
      const response = await secureFetch(
        buildApiUrl(`/api/notes/by-source?sourceType=${encodeURIComponent(sourceType)}&sourceId=${encodeURIComponent(sourceId)}`)
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch note by source');
      }

      const note = await response.json();
      return { id: note.id, title: note.title };
    } catch (error) {
      console.error('Error fetching note for source:', error);
      return null;
    }
  }, []);

  const openOrCreateNoteForSource = useCallback(async (
    sourceType: string,
    sourceId: string,
    sourceName: string,
    initialContent?: string
  ): Promise<void> => {
    const { toast } = await import("sonner");
    const { trackNoteEvent } = await import("@/lib/noteAnalytics");

    try {
      const existingNote = await getNoteForSource(sourceType, sourceId);

      if (existingNote) {
        setActiveNoteId(existingNote.id);
        trackNoteEvent({
          eventType: 'note_viewed',
          noteId: existingNote.id,
          metadata: { source: sourceType, action: 'opened_existing' },
        });
        openNotes();
        return;
      }

      const noteContent = initialContent
        ? {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [{ type: 'text', text: initialContent }],
              },
            ],
          }
        : { type: 'doc', content: [{ type: 'paragraph', content: [] }] };

      const response = await secureFetch(buildApiUrl('/api/notes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${sourceName} — Notes`,
          content: JSON.stringify(noteContent),
          tags: [],
          sourceType,
          sourceId,
          sourceName,
        }),
      });

      if (!response.ok) {
        toast.error("Failed to create note");
        return;
      }

      const note = await response.json();
      setActiveNoteId(note.id);
      trackNoteEvent({
        eventType: 'note_viewed',
        noteId: note.id,
        metadata: { original_event: 'note_created', source: sourceType },
      });
      toast.success("Created new note");
      openNotes();
    } catch (error) {
      console.error('Failed to open or create note:', error);
      toast.error("Failed to open or create note");
    }
  }, [getNoteForSource, openNotes, setActiveNoteId]);

  return (
    <NotesContext.Provider
      value={{
        isNotesOpen,
        openNotes,
        closeNotes,
        toggleNotes,
        captureToNote,
        appendToNote,
        createNewNote,
        activeNoteId,
        setActiveNoteId,
        activeNoteSessionId,
        startNoteSession,
        endNoteSession,
        getNoteForSource,
        openOrCreateNoteForSource,
      }}
    >
      {children}
    </NotesContext.Provider>
  );
};
